import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, useSymbolsContext } from '@punchcard/core';
import { IconClose, IconChevronRight, IconChevronLeft } from '@punchcard/core/icons';
import { useSymbolsModal } from '@punchcard/core/hooks';
import SymbolModalCategoryList from './SymbolModalCategoryList';
import SymbolsScrollableContent from './SymbolsScrollableContent';
import { useTranslation } from 'react-i18next';

interface SymbolsModalProps {
  isOpen: boolean
  onClose: () => void
  onSymbolSelect: (symbol: string) => void
}

const SymbolsModal: React.FC<SymbolsModalProps> = ({ isOpen, onClose, onSymbolSelect }) => {
	const { allSymbolsData, isLoadingCategories, isLoadingAllSymbols, error } = useSymbolsContext();
	const { modalRef } = useSymbolsModal({ isOpen });
	const { t } = useTranslation();
	const [currentCategoryName, setCurrentCategoryName] = useState<string>('');
	const [showCategoryList, setShowCategoryList] = useState<boolean>(false);
	const [isScrollingToCategory, setIsScrollingToCategory] = useState<boolean>(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
	const HEADER_HEIGHT = 45;

	const filteredSymbolsData = useMemo(
		() => allSymbolsData.filter((category) => category.symbols && category.symbols.length > 0),
		[allSymbolsData],
	);
	// Set initial category name when symbols are loaded
	useEffect(() => {
		if (filteredSymbolsData.length > 0 && !currentCategoryName) {
			setCurrentCategoryName(filteredSymbolsData[0].categoryName);
		}
	}, [filteredSymbolsData, currentCategoryName]);

	// Handle ESC key to close category list
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && showCategoryList) {
				setShowCategoryList(false);
			}
		};

		if (showCategoryList) {
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}
	}, [showCategoryList]);

	const handleScroll = useCallback(() => {
		if (!scrollContainerRef.current || filteredSymbolsData.length === 0 || isScrollingToCategory) {
			return;
		}

		const scrollContainer = scrollContainerRef.current;
		const { scrollTop, clientHeight } = scrollContainer;
		let bestCategory = null;
		let maxVisibleArea = 0;

		for (const category of filteredSymbolsData) {
			const element = categoryRefs.current[category.id];
			if (!element) {
				continue;
			}

			const { offsetTop, offsetHeight } = element;
			const visibleTop = Math.max(scrollTop, offsetTop);
			const visibleBottom = Math.min(scrollTop + clientHeight, offsetTop + offsetHeight);
			const visibleArea = Math.max(0, visibleBottom - visibleTop);

			if (visibleArea > maxVisibleArea) {
				maxVisibleArea = visibleArea;
				bestCategory = category;
			}
		}
		if (bestCategory && bestCategory.categoryName !== currentCategoryName) {
			setCurrentCategoryName(bestCategory.categoryName);
		}
	}, [filteredSymbolsData, currentCategoryName, isScrollingToCategory]);

	const handleCategoryChange = (categoryId: number) => {
		const category = filteredSymbolsData.find((cat) => cat.id === categoryId);

		if (category) {
			setCurrentCategoryName(category.categoryName);
			setShowCategoryList(false);
			setIsScrollingToCategory(true); // Prevent scroll handler from overriding

			// Use setTimeout to ensure the symbols view is rendered before scrolling
			setTimeout(() => {
				const categoryElement = categoryRefs.current[categoryId];
				if (categoryElement && scrollContainerRef.current) {
					const scrollContainer = scrollContainerRef.current;

					// Add a one-time event listener for when scrolling ends
					const handleScrollEnd = () => {
						setIsScrollingToCategory(false);
						scrollContainer.removeEventListener('scrollend', handleScrollEnd);
					};

					scrollContainer.addEventListener('scrollend', handleScrollEnd);

					// Scroll to the corresponding section in the symbol list
					scrollContainer.scrollTo({
						top: categoryElement.offsetTop - HEADER_HEIGHT, // Account for header height
						behavior: 'smooth',
					});
				} else {
					setIsScrollingToCategory(false);
				}
			}, 100);
		}
	};

	if (error) {
		if (!isOpen) {
			return null;
		}
		return (
			<div className="position-fixed bg-white border border-danger rounded shadow-lg p-3 symbols-modal__error">
				<div className="text-danger">{error}</div>
			</div>
		);
	}
	if (!isOpen) {
		return null;
	}

	return (
		<div ref={modalRef} className="position-fixed bg-white border shadow-lg symbols-modal">
			{/* Fixed Header with category selector and close button */}
			<div className="border-bottom symbols-modal__header position-sticky top-0 bg-white">
				<div className="d-flex align-items-center w-100 px-2">
					{isLoadingCategories ? (
						<span className="fw-medium">Loading...</span>
					) : (
						<div className="w-100">
							<Button
								className="btn btn-ghost-primary w-100 text-start d-flex justify-content-between align-items-center p-0"
								onClick={() => setShowCategoryList(!showCategoryList)}
							>
								{showCategoryList && <IconChevronLeft className="icon-16 me-2" />}
								<span className="symbols-text text-truncate">{currentCategoryName || 'Select Category'}</span>
								{!showCategoryList && <IconChevronRight className="icon-16" />}
							</Button>
						</div>
					)}
					<Button className="btn-ghost-primary p-1" onClick={onClose} title={t('close')}>
						<IconClose className="icon-16" />
					</Button>
				</div>
			</div>
			{/* Content Area - Category List OR Symbols */}
			<div className="symbols-modal__body">
				{showCategoryList ? (
					<SymbolModalCategoryList
						categories={filteredSymbolsData}
						onCategorySelect={handleCategoryChange}
						onClose={() => setShowCategoryList(false)}
						currentCategoryName={currentCategoryName}
					/>
				) : (
					/* Scrollable Symbols Content */
					<SymbolsScrollableContent
						scrollContainerRef={scrollContainerRef}
						handleScroll={handleScroll}
						isLoadingAllSymbols={isLoadingAllSymbols}
						filteredSymbolsData={filteredSymbolsData}
						categoryRefs={categoryRefs}
						onSymbolSelect={onSymbolSelect}
					/>
				)}
			</div>
		</div>
	);
};

export default SymbolsModal;
